import React, { useState, useCallback, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Divider,
  SkeletonBodyText,
  SkeletonDisplayText,
  Checkbox,
  Badge,
  Link,
} from '@shopify/polaris';
import { useStoreSettings } from '../hooks/useStoreSettings';
import { TierBadge } from '../components/TierBadge';
import { PageEntrance } from '../components/ui';
import './SettingsPage.css';

/**
 * SettingsPage — store notification & account preferences.
 *
 * Sections:
 *  1. Account info   — store name, email (read-only from Shopify)
 *  2. Notifications  — toggle email notification opt-in (emailNotifyEnabled)
 *  3. Current plan   — tier badge with link to Billing
 */
export function SettingsPage(): React.JSX.Element {
  const { settings, isLoading, isError, error, updateSettings, isUpdating, updateError } =
    useStoreSettings();

  // Local toggle shadows so changes feel instant before the server responds
  const [emailEnabled, setEmailEnabled] = useState<boolean>(false);
  const [dailyScanEnabled, setDailyScanEnabled] = useState<boolean>(true);

  // Sync local shadows from server data once loaded
  useEffect(() => {
    if (settings) {
      setEmailEnabled(settings.emailNotifyEnabled);
      setDailyScanEnabled(settings.dailyScanEnabled);
    }
  }, [settings]);

  const handleEmailToggle = useCallback(
    (checked: boolean) => {
      setEmailEnabled(checked);
      updateSettings({ emailNotifyEnabled: checked });
    },
    [updateSettings],
  );

  const handleDailyScanToggle = useCallback(
    (checked: boolean) => {
      setDailyScanEnabled(checked);
      updateSettings({ dailyScanEnabled: checked });
    },
    [updateSettings],
  );

  return (
    <PageEntrance>
    <Page
      title="Settings"
      backAction={{ content: 'Dashboard', url: '/dashboard' }}
    >
      <BlockStack gap="400">
        <div className="z-hero">
          <div className="z-hero-content">
            <h2 className="z-hero-title">⚙️ App Settings</h2>
            <p className="z-hero-subtitle">
              Manage your notification preferences, account details, and integration settings.
            </p>
          </div>
        </div>
        <Layout>
        {/* Error banners */}
        {isError && (
          <Layout.Section>
            <Banner tone="critical">
              {error?.message ?? 'Failed to load settings.'}
            </Banner>
          </Layout.Section>
        )}

        {updateError && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => {}}>
              {updateError.message ?? 'Failed to save settings.'}
            </Banner>
          </Layout.Section>
        )}

        {/* ── Account Info ─────────────────────────────────────── */}
        <Layout.Section>
          {isLoading ? (
            <Card>
              <BlockStack gap="300">
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText lines={3} />
              </BlockStack>
            </Card>
          ) : (
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Account
                </Text>

                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="start">
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" tone="subdued">
                        Store domain
                      </Text>
                      <Text as="p" variant="bodyMd">
                        {settings?.shopDomain ?? '—'}
                      </Text>
                    </BlockStack>
                  </InlineStack>

                  <Divider />

                  <InlineStack align="space-between" blockAlign="start">
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" tone="subdued">
                        Store name
                      </Text>
                      <Text as="p" variant="bodyMd">
                        {settings?.name ?? '—'}
                      </Text>
                    </BlockStack>
                  </InlineStack>

                  <Divider />

                  <InlineStack align="space-between" blockAlign="start">
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" tone="subdued">
                        Contact email
                      </Text>
                      <Text as="p" variant="bodyMd">
                        {settings?.email ?? (
                          <Text as="span" tone="subdued">
                            Not available
                          </Text>
                        )}
                      </Text>
                    </BlockStack>
                    <Badge tone="info" size="small">
                      From Shopify
                    </Badge>
                  </InlineStack>
                </BlockStack>

                <Text as="p" variant="bodySm" tone="subdued">
                  Account details are synced from your Shopify store and cannot be edited here.
                </Text>
              </BlockStack>
            </Card>
          )}
        </Layout.Section>

        {/* ── Plan ─────────────────────────────────────────────── */}
        <Layout.Section>
          {isLoading ? (
            <Card>
              <BlockStack gap="300">
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText lines={2} />
              </BlockStack>
            </Card>
          ) : (
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    Current Plan
                  </Text>
                  {settings && <TierBadge tier={settings.tier} size="large" />}
                </InlineStack>

                <Text as="p" variant="bodyMd" tone="subdued">
                  To upgrade or manage your subscription, visit the{' '}
                  <Link url="/billing">Billing page</Link>.
                </Text>
              </BlockStack>
            </Card>
          )}
        </Layout.Section>

        {/* ── Notifications ─────────────────────────────────────── */}
        <Layout.Section>
          {isLoading ? (
            <Card>
              <BlockStack gap="300">
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText lines={4} />
              </BlockStack>
            </Card>
          ) : (
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Notifications
                </Text>

                <Checkbox
                  label="Email me when a scan completes"
                  helpText={
                    settings?.email
                      ? `Emails will be sent to ${settings.email}`
                      : 'No email address found on your Shopify account — contact Shopify support to add one.'
                  }
                  checked={emailEnabled}
                  onChange={handleEmailToggle}
                  disabled={isUpdating || !settings?.email}
                />

                <Checkbox
                  label="Email me when a bulk job finishes"
                  helpText={
                    settings?.email
                      ? `Emails will be sent to ${settings.email}`
                      : 'No email address found on your Shopify account.'
                  }
                  checked={emailEnabled}
                  onChange={handleEmailToggle}
                  disabled={isUpdating || !settings?.email}
                />

                <Text as="p" variant="bodySm" tone="subdued">
                  {isUpdating
                    ? 'Saving…'
                    : 'Changes are saved automatically.'}
                </Text>
              </BlockStack>
            </Card>
          )}
        </Layout.Section>

        {/* ── Automation ───────────────────────────────────────── */}
        <Layout.Section>
          {isLoading ? (
            <Card>
              <BlockStack gap="300">
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText lines={3} />
              </BlockStack>
            </Card>
          ) : (
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Automation
                </Text>

                <Checkbox
                  label="Enable daily automatic scan"
                  helpText={
                    settings?.tier === 'FREE'
                      ? 'Daily automated scans are available on paid plans only. Upgrade to enable.'
                      : 'Zelium will automatically scan your catalogue every day at 05:00 UTC to keep scores up to date.'
                  }
                  checked={dailyScanEnabled}
                  onChange={handleDailyScanToggle}
                  disabled={isUpdating || settings?.tier === 'FREE'}
                />

                <Text as="p" variant="bodySm" tone="subdued">
                  {isUpdating
                    ? 'Saving…'
                    : 'Changes are saved automatically.'}
                </Text>
              </BlockStack>
            </Card>
          )}
        </Layout.Section>

        </Layout>
      </BlockStack>
    </Page>
    </PageEntrance>
  );
}
